/**
 * User Utility Functions
 * Helper functions for user management
 */

import { UserEntity, UserManagementState } from "../types/user.types";

/**
 * Reset user management state to initial values
 */
export const resetUserState = (): UserManagementState => ({
  isDrawerOpen: false,
  isDeleteModalOpen: false,
  editingUser: null,
  deletingUser: null,
});

/**
 * Open drawer for editing a user
 */
export const openEditDrawer = (user: UserEntity): UserManagementState => ({
  isDrawerOpen: true,
  isDeleteModalOpen: false,
  editingUser: user,
  deletingUser: null,
});

/**
 * Open drawer for creating a new user
 */
export const openCreateDrawer = (): UserManagementState => ({
  isDrawerOpen: true,
  isDeleteModalOpen: false,
  editingUser: null,
  deletingUser: null,
});

/**
 * Close the drawer
 */
export const closeDrawer = (): UserManagementState => ({
  isDrawerOpen: false,
  isDeleteModalOpen: false,
  editingUser: null,
  deletingUser: null,
});

/**
 * Open delete confirmation modal
 */
export const openDeleteModal = (user: UserEntity): UserManagementState => ({
  isDrawerOpen: false,
  isDeleteModalOpen: true,
  editingUser: null,
  deletingUser: user,
});

/**
 * Close delete confirmation modal
 */
export const closeDeleteModal = (): UserManagementState => ({
  isDrawerOpen: false,
  isDeleteModalOpen: false,
  editingUser: null,
  deletingUser: null,
});

/**
 * Get drawer title based on context
 */
export const getDrawerTitle = (editingUser: UserEntity | null): string => {
  return editingUser ? "Редактировать пользователя" : "Добавить пользователя";
};

/**
 * Format user role for display
 */
export const formatUserRole = (role: string): string => {
  const roleLabels: Record<string, string> = {
    REGULAR: "Обычный пользователь",
    ADMIN: "Администратор",
    MULTI_BRANCH_ADMIN: "Администратор всех филиалов",
    SUPER_ADMIN: "Супер администратор",
    DEVELOPER: "Разработчик",
  };
  return roleLabels[role] || role;
};

/**
 * Get user initials from name
 */
export const getUserInitials = (name: string): string => {
  const parts = name.split(" ");
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * Validate email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};
